"use client"

import { useLocale } from "next-intl"
import { useBookingFlow } from "@/components/layout/booking-flow-provider"
import { useCountry } from "@/components/layout/country-provider"
import { CtaBanner } from "@/components/ui/cta-banner"
import { blobMediaUrl } from "@/lib/blob-url"
import { bookingUrlForCountry } from "@/lib/booking"
import { useLocalePath } from "@/lib/use-localized-navigation"

/**
 * The closing banner shared by every page under /tools.
 *
 * On the membership-first site it mirrors the homepage's closing band: the
 * quiz first ("Was passt zu mir?", a white pill on the photo), booking the
 * check-up second. Elsewhere it keeps /pricing first and /contact second.
 * The labels come from the page's own `ctaPrimary` / `ctaSecondary` keys,
 * which the membership-first overlay rewrites to match the new order.
 */
export function CalculatorCtaBanner({
  tool,
  title,
  subtitle,
  primaryLabel,
  secondaryLabel,
}: {
  /** Short tool id (`bmi`, `fib4`…, `tools` for the index): tracking location. */
  tool: string
  title: string
  subtitle: string
  primaryLabel: string
  secondaryLabel: string
}) {
  const lp = useLocalePath()
  const locale = useLocale()
  const { code: country } = useCountry()
  const flow = useBookingFlow()
  const newSite = flow === "membership-first"

  return (
    <CtaBanner
      title={title}
      subtitle={subtitle}
      imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
      primaryCta={{
        label: primaryLabel,
        href: newSite ? lp("/quiz-focus") : lp("/pricing"),
      }}
      secondaryCta={{
        label: secondaryLabel,
        href: newSite
          ? bookingUrlForCountry(
              country,
              locale,
              undefined,
              undefined,
              undefined,
              flow
            )
          : lp("/contact"),
      }}
      primaryOnDark={newSite}
      trackingLocation={`${tool}-closing-banner`}
    />
  )
}

/**
 * Where the membership panel's "see what is included" link goes. The
 * membership-first site dropped /membership (it forwards to the homepage),
 * so there the link opens /pricing, where the plans now live.
 */
export function useMembershipDetailsHref(): string {
  const lp = useLocalePath()
  return useBookingFlow() === "membership-first"
    ? lp("/pricing")
    : lp("/membership")
}
